import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/es';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Button } from '../components/ui/button';
import { Plus, ChevronLeft, ChevronRight, X, Pencil, Trash2, DollarSign, Clock, MapPin, User, Stethoscope, Search, RefreshCw, Ban, Square, PlayCircle } from 'lucide-react';
import { SurgeryDialog } from '../components/SurgeryDialog';
import { BlockDialog } from '../components/BlockDialog';
import { ClinicalNotesDialog } from '../components/ClinicalNotesDialog';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

moment.locale('es');
const API_URL = process.env.REACT_APP_BACKEND_URL;
const localizer = momentLocalizer(moment);

// Definido a nivel de módulo para que la referencia sea ESTABLE entre renders
// (evita que react-big-calendar re-monte todo, que a su vez causaba que el
// buscador perdiera el foco al escribir).
const NullToolbar = () => null;

export function CalendarPage() {
  const { user } = useAuth();
  const canCreate = user?.role !== 'doctor';
  const canEdit = user?.role !== 'doctor';
  const canDelete = ['admin', 'consultora'].includes(user?.role);
  const canEditClinicalNotes = ['admin', 'consultora', 'recepcion', 'enfermero'].includes(user?.role);
  const [surgeries, setSurgeries] = useState([]);
  const [events, setEvents] = useState([]);
  const [pavilions, setPavilions] = useState([]);
  const [pavilionsMap, setPavilionsMap] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSurgery, setSelectedSurgery] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;
  const [currentView, setCurrentView] = useState(isMobile ? 'day' : 'week');
  const [showMiniCalendar, setShowMiniCalendar] = useState(!isMobile);
  const [pavilionFilters, setPavilionFilters] = useState({});
  const [popover, setPopover] = useState({ open: false, event: null, x: 0, y: 0 });
  const popoverRef = useRef(null);
  const [clinicalNotesOpen, setClinicalNotesOpen] = useState(false);
  const [clinicalNotesTarget, setClinicalNotesTarget] = useState(null);
  const [statesMap, setStatesMap] = useState({});
  const [usersMap, setUsersMap] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [includeZeroCost, setIncludeZeroCost] = useState(false);
  const searchRef = useRef(null);
  const [showOnlyMine, setShowOnlyMine] = useState(user?.role === 'doctor');
  const [refreshingPayment, setRefreshingPayment] = useState(false);
  const [blocks, setBlocks] = useState([]);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [blockSlot, setBlockSlot] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statesRes, usersRes, pavRes] = await Promise.all([
        axios.get(`${API_URL}/api/states`, { withCredentials: true }),
        axios.get(`${API_URL}/api/users`, { withCredentials: true }),
        axios.get(`${API_URL}/api/pavilions`, { withCredentials: true }),
      ]);
      const sm = {};
      (statesRes.data || []).forEach(s => { sm[s.id] = s; });
      setStatesMap(sm);
      const um = {};
      (usersRes.data || []).forEach(u => { um[u.id] = u; });
      setUsersMap(um);
      const activePavs = (pavRes.data || []).filter(p => p.is_active !== false);
      setPavilions(activePavs);
      const pm = {};
      const initFilters = {};
      activePavs.forEach(p => {
        pm[p.name] = p;
        initFilters[p.name] = true;
      });
      setPavilionsMap(pm);
      setPavilionFilters(prev => ({ ...initFilters, ...prev }));
    } catch (err) {
      console.error('Error fetching lookup data:', err);
    }
    fetchSurgeries();
    fetchBlocks();
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setPopover({ open: false, event: null, x: 0, y: 0 });
      }
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cancela timers de 429 pendientes al desmontar
  useEffect(() => () => {
    if (refreshRetryTimerRef.current) clearTimeout(refreshRetryTimerRef.current);
  }, []);

  // Cancela timers cuando se cierra el popover para no reintentar sobre un evento ya cerrado
  useEffect(() => {
    if (!popover.open && refreshRetryTimerRef.current) {
      clearTimeout(refreshRetryTimerRef.current);
      refreshRetryTimerRef.current = null;
      refreshRetryAttemptsRef.current = 0;
    }
  }, [popover.open]);

  const fetchBlocks = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/blocks`, { withCredentials: true });
      setBlocks(data);
    } catch (err) {
      console.error('Error fetching blocks:', err);
    }
  };

  const fetchSurgeries = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/surgeries`, {
        withCredentials: true,
      });
      setSurgeries(data);
      
      const calendarEvents = data.map((surgery) => {
        const startDateTime = new Date(`${surgery.date}T${surgery.time}`);
        let endDateTime;
        
        if (surgery.end_time) {
          endDateTime = new Date(`${surgery.date}T${surgery.end_time}`);
        } else {
          endDateTime = new Date(startDateTime.getTime() + 60 * 60 * 1000);
        }
        
        const durationMinutes = Math.round((endDateTime - startDateTime) / (1000 * 60));
        const hours = Math.floor(durationMinutes / 60);
        const minutes = durationMinutes % 60;
        const durationText = hours > 0 ? `${hours}h${minutes > 0 ? ` ${minutes}m` : ''}` : `${minutes}m`;
        
        return {
          id: surgery.id,
          title: `${surgery.patient_name} - ${surgery.surgery_type}`,
          start: startDateTime,
          end: endDateTime,
          resource: surgery,
          duration: durationText,
        };
      });
      setEvents(calendarEvents);
    } catch (error) {
      console.error('Error fetching surgeries:', error);
    }
  };

  const searchResults = searchQuery.trim().length >= 2
    ? surgeries.filter(s => {
        const q = searchQuery.toLowerCase();
        const matches = (
          (s.patient_name || '').toLowerCase().includes(q) ||
          (s.patient_rut || '').toLowerCase().includes(q) ||
          (s.surgery_type || '').toLowerCase().includes(q)
        );
        if (!matches) return false;
        // Checkbox OFF (default): excluir cirugías con presupuesto en cero
        // Checkbox ON: incluir también los de costo cero
        if (!includeZeroCost) {
          const total = Number(s.atencion_total) || 0;
          if (total === 0) return false;
        }
        return true;
      }).slice(0, 8)
    : [];

  const goToSurgery = (surgery) => {
    const date = new Date(`${surgery.date}T${surgery.time || '09:00'}`);
    setCurrentDate(date);
    setCurrentView('day');
    setSearchQuery('');
    setSearchFocused(false);
    // Open popover for this surgery
    const matchingEvent = events.find(e => e.id === surgery.id);
    if (matchingEvent) {
      setPopover({ open: true, event: matchingEvent, x: 500, y: 200 });
    }
  };

  const handleSelectSlot = (slotInfo) => {
    if (!canCreate) return;
    setSelectedSlot(slotInfo);
    setSelectedSurgery(null);
    setDialogOpen(true);
  };

  const handleSelectEvent = useCallback((event, e) => {
    // If it's a block, open edit dialog for blocks
    if (event.resource?._isBlock) {
      setSelectedBlock(event.resource);
      setBlockSlot(null);
      setBlockDialogOpen(true);
      return;
    }
    
    const syntheticEvent = e?.nativeEvent || e;
    let x = 0, y = 0;
    if (syntheticEvent) {
      x = syntheticEvent.clientX || syntheticEvent.pageX || 0;
      y = syntheticEvent.clientY || syntheticEvent.pageY || 0;
    }
    // Clamp position to stay within viewport
    const popW = 380, popH = 320;
    if (x + popW > window.innerWidth) x = window.innerWidth - popW - 20;
    if (y + popH > window.innerHeight) y = y - popH;
    if (x < 10) x = 10;
    if (y < 10) y = 10;

    setPopover({ open: true, event, x, y });
  }, []);

  const openEditDialog = () => {
    setSelectedSurgery(popover.event?.resource);
    setSelectedSlot(null);
    setPopover({ open: false, event: null, x: 0, y: 0 });
    setDialogOpen(true);
  };

  const handleDeleteSurgery = async () => {
    if (!popover.event) return;
    const id = popover.event.resource?.id;
    if (!id) return;
    if (!window.confirm('¿Eliminar esta cirugía?')) return;
    try {
      await axios.delete(`${API_URL}/api/surgeries/${id}`, { withCredentials: true });
      setPopover({ open: false, event: null, x: 0, y: 0 });
      fetchSurgeries();
    } catch (err) {
      console.error('Error deleting surgery:', err);
    }
  };

  const refreshRetryTimerRef = useRef(null);
  const refreshRetryAttemptsRef = useRef(0);
  const MAX_429_RETRIES = 2;

  const handleRefreshPayment = async () => {
    if (!popover.event) return;
    const id = popover.event.resource?.id;
    if (!id) return;
    setRefreshingPayment(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/medilink/refresh-payment/${id}`, {}, { withCredentials: true });
      // Éxito: reset del contador de reintentos
      refreshRetryAttemptsRef.current = 0;
      if (data.updated) {
        fetchSurgeries();
        // Update popover event in place
        setPopover(prev => ({
          ...prev,
          event: {
            ...prev.event,
            resource: {
              ...prev.event.resource,
              atencion_estado_pago: data.estado_pago,
              medilink_confirmed: data.medilink_confirmed,
            }
          }
        }));
      }
      toast.success(data.message || 'Pago actualizado');
    } catch (err) {
      if (err.response?.status === 429) {
        const retryAfter = Number(err.response.headers?.['retry-after']) || 30;
        if (refreshRetryAttemptsRef.current >= MAX_429_RETRIES) {
          refreshRetryAttemptsRef.current = 0;
          toast.error('Medilink sigue saturado. Intenta de nuevo en unos minutos.');
        } else {
          refreshRetryAttemptsRef.current += 1;
          toast.warning(
            `Medilink está saturado (rate limit). Reintentando en ${retryAfter}s… (${refreshRetryAttemptsRef.current}/${MAX_429_RETRIES})`,
            { duration: retryAfter * 1000 }
          );
          if (refreshRetryTimerRef.current) clearTimeout(refreshRetryTimerRef.current);
          refreshRetryTimerRef.current = setTimeout(() => handleRefreshPayment(), retryAfter * 1000);
        }
      } else {
        refreshRetryAttemptsRef.current = 0;
        toast.error(err.response?.data?.detail || 'Error al consultar Medilink');
      }
    } finally {
      setRefreshingPayment(false);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedSurgery(null);
    setSelectedSlot(null);
    fetchData();
  };

  const handleBlockDialogClose = () => {
    setBlockDialogOpen(false);
    setSelectedBlock(null);
    setBlockSlot(null);
    fetchBlocks();
  };

  const openClinicalNotes = () => {
    setClinicalNotesTarget(popover.event?.resource);
    setClinicalNotesOpen(true);
    setPopover({ open: false, event: null, x: 0, y: 0 });
  };


  const eventStyleGetter = (event) => {
    const resource = event.resource;
    
    // Block events
    if (resource._isBlock) {
      const bg = resource.color || '#8B5CF6';
      return {
        style: {
          backgroundColor: bg,
          '--event-bg': bg,
          borderLeft: `4px solid ${bg}`,
          borderRadius: '4px',
          opacity: 0.85,
          color: 'white',
          border: 'none',
          fontSize: '12px',
          padding: '3px 6px',
          cursor: 'pointer',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          overflow: 'hidden',
          lineHeight: '1.3',
        }
      };
    }
    
    // Surgery events
    const surgery = resource;
    let backgroundColor = '#1967D2';
    let borderColor = '#1557B0';
    
    // Priority: doctor's per-pavilion color > doctor's main color > pavilion color > legacy fallback
    const doctorUser = usersMap[surgery.doctor_id];
    const pavSpecificColor = doctorUser?.pavilion_colors?.[surgery.pavilion];
    if (pavSpecificColor) {
      backgroundColor = pavSpecificColor;
      borderColor = pavSpecificColor;
    } else if (doctorUser?.color) {
      backgroundColor = doctorUser.color;
      borderColor = doctorUser.color;
    } else if (pavilionsMap[surgery.pavilion]?.color) {
      backgroundColor = pavilionsMap[surgery.pavilion].color;
      borderColor = pavilionsMap[surgery.pavilion].color;
    } else if (surgery.pavilion === 'Pabellón Capilar') {
      backgroundColor = '#0B8043';
      borderColor = '#0A6E38';
    } else if (surgery.pavilion === 'Pabellón Cirugía Estética') {
      backgroundColor = '#D93025';
      borderColor = '#C5221F';
    }
    
    return {
      style: {
        backgroundColor,
        '--event-bg': backgroundColor,
        borderLeft: `4px solid ${borderColor}`,
        borderRadius: '4px',
        opacity: 0.95,
        color: 'white',
        border: 'none',
        fontSize: '12px',
        padding: '3px 6px',
        cursor: 'pointer',
        boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
        overflow: 'hidden',
        lineHeight: '1.3',
      }
    };
  };

  // Convert blocks to calendar events
  const blockEvents = blocks.flatMap(block => {
    const results = [];
    const startDate = new Date(block.start_date + 'T00:00:00');
    const endDate = new Date(block.end_date + 'T00:00:00');
    
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split('T')[0];
      let start, end;
      
      if (block.all_day || !block.start_time) {
        start = new Date(`${dateStr}T07:00:00`);
        end = new Date(`${dateStr}T21:00:00`);
      } else {
        start = new Date(`${dateStr}T${block.start_time}`);
        end = new Date(`${dateStr}T${block.end_time || block.start_time}`);
      }
      
      results.push({
        id: `block-${block.id}-${dateStr}`,
        title: block.title,
        start,
        end,
        resource: { ...block, _isBlock: true },
      });
    }
    return results;
  });

  const filteredEvents = [
    ...events.filter(event => {
      // Show by default (===false hides). This ensures newly-created pavilions
      // appear on the calendar until the user explicitly unchecks them in the sidebar.
      if (pavilionFilters[event.resource.pavilion] === false) return false;
      if (showOnlyMine && user?.id) {
        return event.resource.doctor_id === user.id || event.resource.consultant_id === user.id;
      }
      return true;
    }),
    ...blockEvents,
  ];

  const handleNavigate = (action) => {
    const newDate = new Date(currentDate);
    if (action === 'PREV') {
      if (currentView === 'month') newDate.setMonth(newDate.getMonth() - 1);
      else if (currentView === 'week') newDate.setDate(newDate.getDate() - 7);
      else newDate.setDate(newDate.getDate() - 1);
    } else if (action === 'NEXT') {
      if (currentView === 'month') newDate.setMonth(newDate.getMonth() + 1);
      else if (currentView === 'week') newDate.setDate(newDate.getDate() + 7);
      else newDate.setDate(newDate.getDate() + 1);
    } else if (action === 'TODAY') {
      setCurrentDate(new Date());
      return;
    }
    setCurrentDate(newDate);
  };

  // Retorna JSX del toolbar. NO es un componente (para evitar remount y pérdida
  // de foco en el buscador cuando `CalendarPage` re-renderiza).
  const renderToolbar = () => {
    const getDateLabel = () => {
      if (currentView === 'month') {
        return moment(currentDate).format('MMMM YYYY');
      } else if (currentView === 'week') {
        const start = moment(currentDate).startOf('week');
        const end = moment(currentDate).endOf('week');
        if (start.month() === end.month()) {
          return `${start.format('D')} - ${end.format('D')} ${start.format('MMMM YYYY')}`;
        } else {
          return `${start.format('D MMM')} - ${end.format('D MMM YYYY')}`;
        }
      } else if (currentView === 'day') {
        return moment(currentDate).format('D [de] MMMM [de] YYYY');
      } else {
        return moment(currentDate).format('MMMM YYYY');
      }
    };

    return (
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3 sm:mb-4 pb-3 sm:pb-4 border-b border-gray-200">
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <Button
            onClick={() => handleNavigate('TODAY')}
            variant="outline"
            className="text-sm font-medium text-gray-700 border-gray-300 hover:bg-gray-50 h-9 px-3"
          >
            Hoy
          </Button>
          
          <div className="flex items-center gap-1">
            <Button
              onClick={() => handleNavigate('PREV')}
              variant="ghost"
              size="sm"
              className="h-9 w-9 p-0 hover:bg-gray-100"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <Button
              onClick={() => handleNavigate('NEXT')}
              variant="ghost"
              size="sm"
              className="h-9 w-9 p-0 hover:bg-gray-100"
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>

          <h2 className="text-base sm:text-xl font-normal text-gray-800 capitalize truncate">
            {getDateLabel()}
          </h2>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          {/* Search bar - hidden on mobile to save space */}
          <div className="relative hidden md:block" ref={searchRef}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                data-testid="calendar-search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
                placeholder="Buscar paciente, RUT o cirugía..."
                className="pl-9 pr-8 w-64 h-9 bg-white border-gray-300 text-sm"
              />
              {searchQuery && (
                <button
                  onClick={() => { setSearchQuery(''); setSearchFocused(false); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            {searchFocused && searchResults.length > 0 && (
              <div className="absolute top-full left-0 mt-1 w-96 bg-white border border-gray-200 rounded-xl shadow-lg z-50 max-h-80 overflow-y-auto" data-testid="search-results-dropdown">
                <label
                  className="flex items-center gap-2 px-4 py-2 border-b border-gray-100 bg-gray-50/60 cursor-pointer select-none"
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <input
                    type="checkbox"
                    checked={includeZeroCost}
                    onChange={(e) => setIncludeZeroCost(e.target.checked)}
                    className="h-3.5 w-3.5 rounded border-gray-300 text-[#1A73E8] focus:ring-[#1A73E8]"
                    data-testid="search-include-zero-cost"
                  />
                  <span className="text-xs text-gray-700">Incluir procedimientos costo cero</span>
                </label>
                {searchResults.map((s) => {
                  const pavColor = pavilionsMap[s.pavilion]?.color || (s.pavilion === 'Pabellón Capilar' ? '#0B8043' : '#D93025');
                  return (
                    <button
                      key={s.id}
                      data-testid={`search-result-${s.id}`}
                      onMouseDown={() => goToSurgery(s)}
                      className="w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center gap-3 border-b border-gray-50 last:border-0"
                    >
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: pavColor }} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">{s.patient_name}</div>
                        <div className="text-xs text-gray-500 truncate">{s.surgery_type} · {s.patient_rut}</div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-xs font-medium text-gray-700">{moment(s.date).format('D MMM')}</div>
                        <div className="text-[11px] text-gray-400">{s.time}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            {searchFocused && searchQuery.trim().length >= 2 && searchResults.length === 0 && (
              <div className="absolute top-full left-0 mt-1 w-80 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden">
                <label
                  className="flex items-center gap-2 px-4 py-2 border-b border-gray-100 bg-gray-50/60 cursor-pointer select-none"
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <input
                    type="checkbox"
                    checked={includeZeroCost}
                    onChange={(e) => setIncludeZeroCost(e.target.checked)}
                    className="h-3.5 w-3.5 rounded border-gray-300 text-[#1A73E8] focus:ring-[#1A73E8]"
                    data-testid="search-include-zero-cost-empty"
                  />
                  <span className="text-xs text-gray-700">Incluir procedimientos costo cero</span>
                </label>
                <p className="text-sm text-gray-500 text-center py-4">No se encontraron resultados</p>
              </div>
            )}
          </div>

          {/* My Surgeries toggle - visible on mobile/tablet (sidebar hidden < lg) */}
          <button
            onClick={() => setShowOnlyMine(!showOnlyMine)}
            className={`lg:hidden inline-flex items-center gap-1.5 h-9 px-2.5 rounded-lg border text-xs font-medium transition-colors whitespace-nowrap ${
              showOnlyMine
                ? 'bg-[#1A73E8] text-white border-[#1A73E8] hover:bg-[#1557B0]'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
            data-testid="filter-my-surgeries-mobile"
            title="Solo mis cirugías"
            aria-pressed={showOnlyMine}
          >
            <User size={14} />
            <span>Mías</span>
          </button>

          <div className="flex bg-gray-100 rounded-lg p-1 overflow-x-auto">
            {['day', 'week', 'month', 'agenda'].map((view) => (
              <button
                key={view}
                onClick={() => setCurrentView(view)}
                className={`px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                  currentView === view
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {view === 'day' ? 'Día' : view === 'week' ? 'Semana' : view === 'month' ? 'Mes' : 'Agenda'}
              </button>
            ))}
          </div>

          {canCreate && (
            <>
              <Button
                onClick={() => {
                  setSelectedSurgery(null);
                  setSelectedSlot(null);
                  setDialogOpen(true);
                }}
                className="bg-[#1A73E8] hover:bg-[#1557B0] text-white shadow-md h-9 px-3"
                data-testid="add-surgery-button"
              >
                <Plus className="sm:mr-2" size={18} />
                <span className="hidden sm:inline">Crear</span>
              </Button>
              <Button
                onClick={() => {
                  setSelectedBlock(null);
                  setBlockSlot(null);
                  setBlockDialogOpen(true);
                }}
                variant="outline"
                className="border-[#8B5CF6] text-[#8B5CF6] hover:bg-[#8B5CF6]/10 h-9 px-3"
                data-testid="add-block-button"
              >
                <Ban className="sm:mr-2" size={16} />
                <span className="hidden sm:inline">Bloqueo</span>
              </Button>
            </>
          )}
        </div>
      </div>
    );
  };

  const CustomEvent = useCallback(({ event }) => {
    const resource = event.resource;
    
    // Block event rendering
    if (resource._isBlock) {
      return (
        <div className="flex flex-col h-full overflow-hidden leading-tight py-0.5" title={resource.title}>
          <div className="font-semibold text-white text-[12px]" style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
            {resource.title}
          </div>
          {resource.doctor_name && (
            <div className="text-[11px] text-white/80">{resource.doctor_name}</div>
          )}
          {!resource.all_day && resource.start_time && (
            <div className="text-[10px] text-white/70 mt-1">
              {resource.start_time} – {resource.end_time}
            </div>
          )}
        </div>
      );
    }
    
    // Surgery event rendering
    const surgery = resource;
    const state = statesMap[surgery.state_id];
    const stateName = state?.name || '';
    const stateColor = state?.color || '#6A736B';
    let payLabel = '';
    if (surgery.atencion_estado_pago) payLabel = surgery.atencion_estado_pago;
    else if (surgery.medilink_confirmed) payLabel = 'Pagado';

    const tooltipText = [
      surgery.patient_name,
      surgery.surgery_type,
      `${moment(event.start).format('HH:mm')} – ${moment(event.end).format('HH:mm')}`,
      surgery.pavilion,
      stateName,
      payLabel,
    ].filter(Boolean).join(' | ');

    return (
      <div className="flex flex-col h-full overflow-hidden leading-tight py-0.5" title={tooltipText}>
        <div className="font-semibold text-white text-[12px]" style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
          {surgery.patient_name}
        </div>
        <div className="text-[11px] text-white/90" style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
          {surgery.surgery_type}
        </div>
        <div className="flex items-center gap-1 mt-1 flex-wrap">
          {stateName && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full text-white font-medium" style={{ backgroundColor: stateColor, opacity: 0.85 }}>
              {stateName}
            </span>
          )}
          {payLabel && (
            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${
              payLabel === 'Pagado Completo' ? 'bg-white/30 text-white' :
              payLabel === 'Abono' ? 'bg-yellow-300/40 text-white' :
              'bg-red-400/40 text-white'
            }`}>
              {payLabel}
            </span>
          )}
        </div>
        {/* Time range at bottom for tall events, gives context in the empty middle area */}
        <div className="text-[10px] text-white/70 mt-1">
          {moment(event.start).format('HH:mm')} – {moment(event.end).format('HH:mm')}
        </div>
      </div>
    );
  }, [statesMap]);

  // Memoizar el objeto `components` para que su referencia NO cambie en cada
  // render (evita re-mount de react-big-calendar y pérdida de foco en inputs).
  const calendarComponents = useMemo(() => ({
    event: CustomEvent,
    toolbar: NullToolbar,
  }), [CustomEvent]);

  return (
    <div className="h-screen flex flex-col bg-white">
      {/* Header */}
      <div className="px-3 sm:px-6 pt-4 sm:pt-6 pb-0">
        {renderToolbar()}
      </div>

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar (desktop only) */}
        {showMiniCalendar && (
          <div className="hidden lg:block w-64 border-r border-gray-200 p-4 overflow-y-auto">
            {/* Mini Calendar */}
            <div className="mb-6">
              <Calendar
                onChange={setCurrentDate}
                value={currentDate}
                className="mini-calendar border-none"
                locale="es-ES"
              />
            </div>

            {/* Pabellones Filter */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-900 mb-2">Mis calendarios</h3>

              {pavilions.map((p) => (
                <label key={p.id} className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={!!pavilionFilters[p.name]}
                    onChange={(e) => setPavilionFilters({
                      ...pavilionFilters,
                      [p.name]: e.target.checked,
                    })}
                    className="w-4 h-4 rounded border-gray-300"
                    style={{ accentColor: p.color }}
                  />
                  <div className="flex items-center gap-2 flex-1">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: p.color }}></div>
                    <span className="text-sm text-gray-700 group-hover:text-gray-900">{p.name}</span>
                  </div>
                </label>
              ))}
            </div>

            {/* My Surgeries Filter */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <h3 className="text-sm font-medium text-gray-900 mb-2">Filtro</h3>
              <label className="flex items-center gap-3 cursor-pointer group" data-testid="filter-my-surgeries">
                <input
                  type="checkbox"
                  checked={showOnlyMine}
                  onChange={(e) => setShowOnlyMine(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-[#1A73E8] focus:ring-[#1A73E8]"
                />
                <div className="flex items-center gap-2 flex-1">
                  <User className="w-4 h-4 text-[#1A73E8]" />
                  <span className="text-sm text-gray-700 group-hover:text-gray-900">Solo mis cirugías</span>
                </div>
              </label>
            </div>
          </div>
        )}

        {/* Calendar */}
        <div className="flex-1 p-2 sm:p-6 overflow-auto">
          <BigCalendar
            localizer={localizer}
            events={filteredEvents}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%', minHeight: 600 }}
            onSelectSlot={handleSelectSlot}
            onSelectEvent={handleSelectEvent}
            selectable={canCreate}
            view={currentView}
            onView={setCurrentView}
            date={currentDate}
            onNavigate={setCurrentDate}
            views={['month', 'week', 'day', 'agenda']}
            eventPropGetter={eventStyleGetter}
            components={calendarComponents}
            step={30}
            timeslots={2}
            min={new Date(2024, 0, 1, 7, 0, 0)}
            max={new Date(2024, 0, 1, 21, 0, 0)}
            messages={{
              next: 'Siguiente',
              previous: 'Anterior',
              today: 'Hoy',
              month: 'Mes',
              week: 'Semana',
              day: 'Día',
              agenda: 'Agenda',
              date: 'Fecha',
              time: 'Hora',
              event: 'Cirugía',
              noEventsInRange: 'No hay cirugías programadas',
              allDay: 'Todo el día',
              work_week: 'Semana laboral',
              yesterday: 'Ayer',
              tomorrow: 'Mañana',
              showMore: (total) => `+${total} más`
            }}
            formats={{
              dayFormat: 'ddd D',
              dayHeaderFormat: 'dddd D [de] MMMM',
              dayRangeHeaderFormat: ({ start, end }) => 
                `${moment(start).format('D MMM')} - ${moment(end).format('D MMM YYYY')}`,
              agendaDateFormat: 'ddd D MMM',
              agendaTimeFormat: 'HH:mm',
              agendaTimeRangeFormat: ({ start, end }) => 
                `${moment(start).format('HH:mm')} - ${moment(end).format('HH:mm')}`,
              eventTimeRangeFormat: () => '',
            }}
          />
        </div>
      </div>

      {/* Event Popover */}
      {popover.open && popover.event && (() => {
        const surgery = popover.event.resource;
        const pavilionColor = pavilionsMap[surgery.pavilion]?.color || (surgery.pavilion === 'Pabellón Capilar' ? '#0B8043' : '#D93025');
        const state = statesMap[surgery.state_id];
        const doctor = usersMap[surgery.doctor_id];
        const consultora = usersMap[surgery.consultant_id];
        let payLabel = '';
        if (surgery.atencion_estado_pago) payLabel = surgery.atencion_estado_pago;
        else if (surgery.medilink_confirmed) payLabel = 'Pagado';
        return (
          <div
            ref={popoverRef}
            data-testid="event-popover"
            className="fixed z-50 bg-white rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.18)] border border-gray-200 w-[400px]"
            style={{ left: popover.x, top: popover.y }}
          >
            {/* Header */}
            <div className="flex items-center justify-end gap-1 px-3 pt-3">
              {canEditClinicalNotes && (
                <button onClick={openClinicalNotes} className="p-1.5 rounded-full hover:bg-emerald-50" title="Notas Clínicas" data-testid="popover-clinical-notes-btn">
                  <Stethoscope className="h-4 w-4 text-emerald-700" />
                </button>
              )}
              {canEdit && (
                <button onClick={openEditDialog} className="p-1.5 rounded-full hover:bg-gray-100" title="Editar" data-testid="popover-edit-btn">
                  <Pencil className="h-4 w-4 text-gray-500" />
                </button>
              )}
              {canDelete && (
                <button onClick={handleDeleteSurgery} className="p-1.5 rounded-full hover:bg-gray-100" title="Eliminar" data-testid="popover-delete-btn">
                  <Trash2 className="h-4 w-4 text-gray-500" />
                </button>
              )}
              <button onClick={() => setPopover({ open: false, event: null, x: 0, y: 0 })} className="p-1.5 rounded-full hover:bg-gray-100" data-testid="popover-close-btn">
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="px-5 pb-5 space-y-2.5">
              {/* Title row */}
              <div className="flex items-start gap-3">
                <div className="w-3 h-3 rounded-sm mt-1.5 flex-shrink-0" style={{ backgroundColor: pavilionColor }}></div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 leading-snug">
                    {surgery.patient_name}
                  </h3>
                  <p className="text-sm text-gray-500">{surgery.patient_rut}</p>
                </div>
              </div>

              {/* Surgery type */}
              <div className="flex items-center gap-3 text-sm text-gray-700">
                <div className="w-3 flex-shrink-0"></div>
                <Stethoscope className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                <span className="font-medium">{surgery.surgery_type}</span>
              </div>

              {/* Date & Time */}
              <div className="flex items-center gap-3 text-sm text-gray-700">
                <div className="w-3 flex-shrink-0"></div>
                <Clock className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                <span>
                  {moment(popover.event.start).format('dddd D [de] MMMM')} · {moment(popover.event.start).format('HH:mm')} – {moment(popover.event.end).format('HH:mm')}
                </span>
              </div>

              {/* Pavilion */}
              <div className="flex items-center gap-3 text-sm text-gray-700">
                <div className="w-3 flex-shrink-0"></div>
                <MapPin className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                <span>{surgery.pavilion}</span>
              </div>

              {/* State */}
              {state && (
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-3 flex-shrink-0"></div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: state.color }}></div>
                    <span className="font-medium text-gray-700">{state.name}</span>
                  </div>
                </div>
              )}

              {/* Doctor & Consultora */}
              {(doctor || consultora) && (
                <div className="flex items-center gap-3 text-sm text-gray-700">
                  <div className="w-3 flex-shrink-0"></div>
                  <User className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                  <span>
                    {doctor && <span>Dr. {doctor.name}</span>}
                    {doctor && consultora && <span className="text-gray-300 mx-1">·</span>}
                    {consultora && <span>{consultora.name}</span>}
                  </span>
                </div>
              )}

              {/* Payment status */}
              {payLabel && (
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-3 flex-shrink-0"></div>
                  <DollarSign className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                  <div className="flex items-center gap-1.5">
                    <span className={
                      payLabel === 'Pagado Completo' ? 'text-emerald-700 font-medium' :
                      payLabel === 'Abono' ? 'text-amber-700 font-medium' :
                      payLabel === 'Pendiente' ? 'text-red-700 font-medium' :
                      'text-gray-700'
                    }>{payLabel}</span>
                    {surgery.atencion_nombre && (
                      <span className="text-gray-400 text-xs">· {surgery.atencion_nombre}</span>
                    )}
                  </div>
                </div>
              )}

              {/* Refresh Payment Button */}
              {surgery.patient_rut && (
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-3 flex-shrink-0"></div>
                  <button
                    onClick={handleRefreshPayment}
                    disabled={refreshingPayment}
                    data-testid="refresh-payment-btn"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#1A73E8] bg-blue-50 hover:bg-blue-100 rounded-md transition-colors disabled:opacity-50"
                  >
                    <RefreshCw className={`h-3 w-3 ${refreshingPayment ? 'animate-spin' : ''}`} />
                    {refreshingPayment ? 'Consultando...' : 'Actualizar pago'}
                  </button>
                </div>
              )}

              {/* Allergies */}
              {surgery.allergies && (
                <div className="flex items-start gap-3 text-sm pt-1 border-t border-gray-100" data-testid="popover-allergies">
                  <span className="text-red-600 font-semibold text-xs mt-0.5">⚠ Alergias:</span>
                  <p className="whitespace-pre-wrap text-xs text-red-800 flex-1">{surgery.allergies}</p>
                </div>
              )}

              {/* Bed */}
              {surgery.bed && (
                <div className="flex items-center gap-3 text-sm text-gray-700" data-testid="popover-bed">
                  <span className="text-xs font-medium text-[#2B4C3B]">🛏 Cama:</span>
                  <p className="text-xs">{surgery.bed}</p>
                </div>
              )}

              {/* Observations */}
              {surgery.observations && (
                <div className="flex items-start gap-3 text-sm text-gray-600 pt-1 border-t border-gray-100">
                  <div className="w-3 flex-shrink-0"></div>
                  <p className="whitespace-pre-wrap text-xs">{surgery.observations}</p>
                </div>
              )}

              {/* Estado en curso / finalizado (informativo). Iniciar/Finalizar viven en Gestión Clínica. */}
              {(surgery.started_at || surgery.ended_at) && (
                <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                  {surgery.started_at && !surgery.ended_at && (
                    <div className="flex-1 flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-emerald-700 bg-emerald-50 rounded-md" data-testid="popover-in-progress">
                      <PlayCircle className="h-3.5 w-3.5 animate-pulse" />
                      En curso desde {moment.utc(surgery.started_at).local().format('HH:mm')}
                    </div>
                  )}
                  {surgery.ended_at && (
                    <div className="flex-1 flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-600 bg-gray-50 rounded-md" data-testid="popover-finished">
                      <Square className="h-3.5 w-3.5" fill="currentColor" />
                      Finalizada a las {moment.utc(surgery.ended_at).local().format('HH:mm')}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {clinicalNotesOpen && (
        <ClinicalNotesDialog
          surgery={clinicalNotesTarget}
          open={clinicalNotesOpen}
          onClose={() => { setClinicalNotesOpen(false); setClinicalNotesTarget(null); }}
          onSaved={() => fetchData()}
        />
      )}

      {dialogOpen && (
        <SurgeryDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          surgery={selectedSurgery}
          slot={selectedSlot}
        />
      )}

      {blockDialogOpen && (
        <BlockDialog
          open={blockDialogOpen}
          onClose={handleBlockDialogClose}
          block={selectedBlock}
          slot={blockSlot}
        />
      )}
    </div>
  );
}
